import { Feature, Geolocation } from 'ol';
import { Point } from 'ol/geom';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import * as React from 'react';
import { forwardRef, useEffect, useMemo } from 'react';
import { GeolocationState, Severity, VegkartError } from '@/state';
import { FeatureLayerType, FeatureLayerTypeKey } from '../layers/FeatureLayerType';
import { userPositionStyle } from '../style/UserPositionStyle';
import { useMap } from '@/state/context/MapContext';
import { handleError } from '@/actions/metaActions';
import { setGeolocationState } from '@/actions/overlayActions';
import { unByKey } from 'ol/Observable';
import useMapLayer from '../layers/useMapLayer';
import { stateGeolocationState } from '@/selectors/selectors';
import { useDispatch, useSelector } from '@/state/store';

const CurrentPosition = forwardRef<HTMLButtonElement>((_, ref) => {
    const { map } = useMap();
    const geolocationState = useSelector(stateGeolocationState);
    const dispatch = useDispatch();
    const geolocation = useMemo(() => {
        const gl = new Geolocation({
            trackingOptions: {
                enableHighAccuracy: true,
            },
            projection: map.getView().getProjection(),
        });
        gl.on('error', error => {
            const geolocationError = toSerializableGeolocationError(error);

            // If the error code is 2 (position unavailable) but we have a position, it might be a transient error. In that case, we choose not to dispatch an error notification.
            const hasPosition = gl.getPosition();
            const isTransientPositionUnavailable =
                geolocationError.code === 2 && hasPosition;

            if (isTransientPositionUnavailable) return;

            dispatch(
                handleError(
                    new VegkartError(
                        getErrorMessage(error as unknown as GeolocationPositionError),
                        { geolocationError },
                        Severity.ERROR
                    )
                )
            );
        });
        return gl;
    }, [dispatch, map]);

    const positionFeature = useMemo(() => {
        const feature = new Feature();
        feature.setStyle(userPositionStyle);
        feature.set(FeatureLayerTypeKey, FeatureLayerType.USER_POSITION);
        return feature;
    }, []);

    const accuracyFeature = useMemo(() => {
        const feature = new Feature();
        feature.set(FeatureLayerTypeKey, FeatureLayerType.USER_POSITION);
        return feature;
    }, []);

    const layer = useMemo(
        () =>
            new VectorLayer({
                source: new VectorSource({
                    features: [positionFeature, accuracyFeature],
                }),
            }),
        [positionFeature, accuracyFeature]
    );
    useMapLayer({ layer, index: 1 });

    function enableFollowMe() {
        const wasTracking = geolocation.getTracking();
        if (!wasTracking) {
            geolocation.setTracking(true);
        }

        if (wasTracking && geolocation.getPosition()) {
            map.getView().setCenter(geolocation.getPosition()!);
        } else {
            // Just (re-)started tracking — cached position may be stale, wait for fresh GPS data
            geolocation.once('change:position', () => {
                const newCoordinates = geolocation.getPosition();
                if (newCoordinates) {
                    map.getView().setCenter(newCoordinates);
                }
            });
        }

        layer.setVisible(true);
        dispatch(setGeolocationState(GeolocationState.Follow));
    }

    function disableFollowMe() {
        dispatch(setGeolocationState(GeolocationState.Active));
        if (geolocation.getTracking() === true) {
            geolocation.setTracking(false);
        }
        layer.setVisible(false);
    }

    function toggleFollowMe() {
        if (geolocationState === GeolocationState.Follow) {
            disableFollowMe();
        } else {
            enableFollowMe();
        }
    }
    useEffect(() => {
        const events = [
            geolocation.on('change:position', () => {
                const coordinates = geolocation.getPosition();
                if (coordinates) positionFeature.setGeometry(new Point(coordinates));
            }),
            geolocation.on('change:accuracyGeometry', () => {
                const accuracyGeometry = geolocation.getAccuracyGeometry();
                if (accuracyGeometry) {
                    accuracyFeature.setGeometry(accuracyGeometry);
                }
            }),
        ];
        return () => {
            events.forEach(e => unByKey(e));
        };
    }, [accuracyFeature, geolocation, map, positionFeature]);

    if ('geolocation' in navigator) {
        const isActivated = geolocationState === GeolocationState.Follow;
        const isActivatedClass = isActivated
            ? ' c-current-position__get-position--active'
            : '';
        const isActivatedIcon = isActivated
            ? 'o-icon-gps-tracked'
            : 'o-icon-gps-untracked';
        return (
            <button
                ref={ref}
                aria-pressed={isActivated}
                title={'Vis min posisjon'}
                type="button"
                className={`c-current-position__get-position o-btn-square${isActivatedClass} c-map-controls__button`}
                onClick={toggleFollowMe}
            >
                <i className={isActivatedIcon + ' c-current-position__gps-icon'} />
            </button>
        );
    } else {
        return null;
    }
});
function getErrorMessage(error: GeolocationPositionError) {
    switch (error.code) {
        case 1:
            return 'Vi har ikke tilgang til din lokasjon.';
        case 2:
            return 'Lokasjon ikke tilgjengelig.';
        case 3:
            return 'Tidsavbrudd ved henting av lokasjon.';
        default:
            return 'Kunne ikke hente lokasjon.';
    }
}

function toSerializableGeolocationError(error: unknown): {
    code?: number;
    message?: string;
    name?: string;
} {
    if (!error || typeof error !== 'object') {
        return {};
    }

    const errorLike = error as {
        code?: unknown;
        message?: unknown;
        name?: unknown;
    };

    return {
        code: typeof errorLike.code === 'number' ? errorLike.code : undefined,
        message: typeof errorLike.message === 'string' ? errorLike.message : undefined,
        name: typeof errorLike.name === 'string' ? errorLike.name : undefined,
    };
}

CurrentPosition.displayName = 'CurrentPosition';
export { CurrentPosition };
